import type { Role } from './auth';

/**
 * What this account may do, decided before a control is offered.
 *
 * The session role was parsed, validated, and then used only as a label beside
 * the user's name. Every write control rendered for everyone, so a viewer
 * discovered their permissions by pressing something irreversible-looking and
 * reading a 403 afterwards.
 *
 * This is the global half of the answer and only the global half. Backend
 * authorization is two-sided: a global role and a role on the company that
 * owns the bucket, and a 403 never says which one refused. So a `true` here
 * means "your account role does not forbid this" and never "this will
 * succeed" - the company grant is still checked at the boundary, and the
 * refusal path stays exactly as it is.
 *
 * Read the other way it is exact: when this says no, the write is certain to
 * be refused, and that is the case worth acting on.
 */

export type Ability =
  /** Start runs, register populations, decide flags, name, evaluate the gate. */
  | 'operate'
  /** Approve documents and confirm attributions. */
  | 'review'
  /** Copy a package into a buyer's bucket. */
  | 'deliver'
  /** Register buckets, roles, sweep selection. */
  | 'administer';

const RANK: Record<Role, number> = {
  viewer: 0,
  reviewer: 1,
  operator: 2,
  admin: 3,
};

const NEEDS: Record<Ability, number> = {
  review: 1,
  operate: 2,
  deliver: 3,
  administer: 3,
};

export function can(role: Role | undefined, ability: Ability): boolean {
  // An unknown role is not a permitted one. A session still loading must not
  // render a control that a viewer would then be refused.
  if (!role) return false;
  return (RANK[role] ?? 0) >= NEEDS[ability];
}

/** Why a control is unavailable, in the operator's terms. */
export function cannotBecause(role: Role | undefined, ability: Ability): string {
  const need: Record<Ability, string> = {
    review: 'reviewer',
    operate: 'operator',
    deliver: 'an administrator',
    administer: 'an administrator',
  };
  if (!role) return 'Checking what your account is allowed to do.';
  return `Your account is a ${role} and this needs ${need[ability]}. Ask an administrator to raise your role.`;
}
